import { useCallback, useEffect, useReducer, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/capacitor';
import { useTranslation, Trans } from 'react-i18next';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Network } from '@capacitor/network';
import {
  initPushNotifications,
  isPushNotificationsEnabled,
} from 'shared/initPushNotifications';
import { useNotifications } from 'features/notifications/hooks/useNotifications';
import { useNotificationsSettings } from 'features/notifications/hooks/useNotificationsSettings';
import { getPushTokenFromLocalStorage } from 'state/localStorage';
import { NotificationSetting } from 'features/notifications/notifications.types';
import { useAppInfo } from 'hooks/useAppInfo';
import { getDeviceInfo } from 'state/localStorageTyped';
import { useAuthState } from 'state/useAuthState';
import { API } from 'api';
import { useWaterLevelEnabled } from 'features/waterLevel/hooks/useWaterLevelEnabled';
import { useWeatherAlertList } from 'features/weatherAlerts/hooks/useWeatherAlertList';
import { WeatherAlerts } from 'features/weatherAlerts/weatherAlerts.types';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import {
  openNativeAppSettings,
  openNativeAppNotificationSettings,
} from 'shared/utils';
import { useHistory } from 'react-router-dom';
import { AlertCounts, NotificationKeys, NotificationStatus } from './types';
import { getInitialNotificationsState, reducer, getEmailLink } from './utils';

type NotificationItemProps = {
  title: string;
  status?: NotificationStatus;
};

type EnabledItemProps = {
  title: string;
  enabled: boolean;
};

type ErrorAlertProps = {
  notificationKey: NotificationKeys;
};

type WeatherAlertMessageProps = {
  alerts: WeatherAlerts;
};

const NotificationItem = (props: NotificationItemProps): JSX.Element => {
  const { title, status = 'idle' } = props;
  const { t } = useTranslation();

  const renderIcon = (): JSX.Element => {
    switch (status) {
      case 'pending':
        return <CircularProgress size={21} />;
      case 'success':
        return (
          <CheckCircleIcon
            color="success"
            role="img"
            aria-label={t('testNotifications.aria.success')}
          />
        );
      case 'error':
        return (
          <CancelIcon
            color="error"
            role="img"
            aria-label={t('testNotifications.aria.error')}
          />
        );
      default:
        return (
          <RemoveCircleIcon
            color="disabled"
            role="img"
            aria-label={t('testNotifications.aria.idle')}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography>{title}</Typography>
      {renderIcon()}
    </Box>
  );
};

const EnabledItem = (props: EnabledItemProps): JSX.Element => {
  const { title, enabled } = props;
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography>{title}</Typography>
      {enabled ? (
        <CheckCircleIcon
          color="success"
          role="img"
          aria-label={t('testNotifications.aria.enabled')}
        />
      ) : (
        <ErrorOutlineIcon
          color="warning"
          role="img"
          aria-label={t('testNotifications.aria.disabled')}
        />
      )}
    </Box>
  );
};

const ErrorAlert = (props: ErrorAlertProps): JSX.Element => {
  const { notificationKey } = props;
  const { t } = useTranslation();

  if (notificationKey === 'notificationSettings') {
    return (
      <Alert
        icon={<ErrorIcon />}
        severity="error"
        data-testid={`alert-error-${notificationKey}`}
      >
        <AlertTitle>
          {t('testNotifications.alerts.notificationSettings.title')}
        </AlertTitle>
        <Typography>
          <Trans i18nKey="testNotifications.alerts.notificationSettings.description">
            Please enable notifications in
            {/* the linter doesn't know component="button" renders a button under the hood instead of <a> */}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              component="button"
              color="inherit"
              onClick={openNativeAppSettings}
              underline="none"
              sx={{
                fontWeight: 'bold',
                verticalAlign: 'baseline',
              }}
            >
              Settings
            </Link>
          </Trans>
        </Typography>
      </Alert>
    );
  }

  return (
    <Alert
      icon={<ErrorIcon />}
      severity="error"
      data-testid={`alert-error-${notificationKey}`}
    >
      <AlertTitle>
        {t(`testNotifications.alerts.${notificationKey}.title`)}
      </AlertTitle>
      <Typography>
        {t(`testNotifications.alerts.${notificationKey}.description`)}
      </Typography>
    </Alert>
  );
};

const getAlertCounts = (alerts: WeatherAlerts): AlertCounts => {
  const alertCountKeys: Array<keyof AlertCounts> = [
    'aqi',
    'atmospheric',
    'waterLevel',
  ];

  return alertCountKeys.reduce((acc, key) => {
    acc[key] = alerts.filter(
      (alert) => alert.station.weatherStationType === key && alert.isEnabled,
    ).length;
    return acc;
  }, {} as AlertCounts);
};

const WeatherAlertsMessage = (
  props: WeatherAlertMessageProps,
): JSX.Element | null => {
  const waterLevelEnabled = useWaterLevelEnabled();
  const { t } = useTranslation();

  const { alerts } = props;
  if (!alerts.length) return null;

  const alertCounts = getAlertCounts(alerts);
  return (
    <>
      {waterLevelEnabled && (
        <Typography sx={{ fontWeight: 'bold' }}>
          {t('testNotifications.alerts.success.waterLevel', {
            count: alertCounts.waterLevel,
          })}
        </Typography>
      )}
      <Typography sx={{ fontWeight: 'bold' }}>
        {t('testNotifications.alerts.success.weatherStations', {
          count: alertCounts.atmospheric,
        })}
      </Typography>
      <Typography sx={{ fontWeight: 'bold' }}>
        {t('testNotifications.alerts.success.aqi', {
          count: alertCounts.aqi,
        })}
      </Typography>
    </>
  );
};

const getWarningTypeKey = (
  criticalAlertsEnabled: boolean,
  timeSensitiveEnabled: boolean,
): string => {
  if (!timeSensitiveEnabled && !criticalAlertsEnabled) {
    return 'testNotifications.alerts.enablementWarning.type.both';
  }

  return !criticalAlertsEnabled
    ? 'testNotifications.alerts.enablementWarning.type.critical'
    : 'testNotifications.alerts.enablementWarning.type.timeSensitive';
};

const AlertStatusWarning = (props: {
  criticalAlertsEnabled: boolean;
  timeSensitiveEnabled: boolean;
}): JSX.Element => {
  const { criticalAlertsEnabled, timeSensitiveEnabled } = props;
  const { t } = useTranslation();

  const warningTypeKey = getWarningTypeKey(
    criticalAlertsEnabled,
    timeSensitiveEnabled,
  );

  return (
    <Alert
      icon={<ErrorOutlineIcon />}
      severity="warning"
      data-testid="alert-warning"
    >
      <AlertTitle sx={{ fontWeight: 'fontWeightBold' }}>
        {t('testNotifications.alerts.enablementWarning.title')}
      </AlertTitle>
      <Stack gap={1} sx={{ justifyContent: 'start', alignItems: 'start' }}>
        <Box>
          <Trans
            i18nKey={warningTypeKey}
            components={[<strong key="0" />, <strong key="1" />]}
          />
          {t('testNotifications.alerts.enablementWarning.message')}
        </Box>
        {/* the linter doesn't know component="button" renders a button under the hood */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          color="textPrimary"
          component="button"
          fontWeight="fontWeightBold"
          onClick={openNativeAppNotificationSettings}
          variant="body2"
        >
          {t('notifications.delivery.enable')}
        </Link>
      </Stack>
    </Alert>
  );
};

const sendErrorReport = (link: string): void => {
  const newWindow = window.open(link, '_blank');
  if (newWindow) {
    newWindow.opener = null;
  }
};

export const TestNotificationsContent = (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const [countySubscriptions, setCountySubscriptions] = useState<
    NotificationSetting[]
  >([]);
  const [criticalAlertStatus, setCriticalAlertStatus] =
    useState<NotificationStatus>('idle');
  const [state, dispatch] = useReducer(reducer, {
    testCount: 0,
    notificationsState: getInitialNotificationsState('idle'),
  });
  const pushToken = getPushTokenFromLocalStorage();
  const { subscribedNotificationSettings } = useNotifications({
    retryQuery: false,
    queryEnabled: false,
  });
  const { user } = useAuthState();
  const appInfo = useAppInfo();
  const deviceInfo = getDeviceInfo();
  const { alerts = [] } = useWeatherAlertList();
  const isCriticalAlertsReleaseFlagEnabled = useReleaseFlag(
    'enable_critical_alerts',
  );
  const {
    criticalSettings: { criticalAlertsEnabled, timeSensitiveEnabled },
    isIOS,
  } = useNotificationsSettings({
    regionSettingsQueryEnabled: false,
  });

  const showAlertStatus = isCriticalAlertsReleaseFlagEnabled && isIOS;
  const showAlertStatusWarning =
    showAlertStatus && (!timeSensitiveEnabled || !criticalAlertsEnabled);

  const testNotificationSettings = useCallback(async () => {
    try {
      const isEnabled = await isPushNotificationsEnabled();
      if (!isEnabled) throw new Error();
      dispatch({ notificationKey: 'notificationSettings', status: 'success' });
    } catch (err) {
      dispatch({ notificationKey: 'notificationSettings', status: 'error' });
      throw err;
    }
  }, []);

  const testDeviceRegistration = useCallback(async () => {
    try {
      if (!pushToken) throw new Error();

      // ensure push notifications state is updated
      await initPushNotifications(history);
      // check if the push token changed
      const updatedPushToken = getPushTokenFromLocalStorage();
      if (updatedPushToken !== pushToken) {
        Sentry.captureMessage('push-token-was-outdated', { level: 'warning' });
      }
      dispatch({ notificationKey: 'deviceRegistration', status: 'success' });
    } catch (err) {
      dispatch({ notificationKey: 'deviceRegistration', status: 'error' });
      throw err;
    }
  }, [pushToken, history]);

  const testCountySubscriptions = useCallback(async () => {
    try {
      const response = await subscribedNotificationSettings.refetch();
      const notifSubscriptions = response?.data?.data ?? [];
      if (notifSubscriptions.length === 0) {
        dispatch({ notificationKey: 'countySubscriptions', status: 'idle' });
        return;
      }
      setCountySubscriptions(notifSubscriptions);
      dispatch({ notificationKey: 'countySubscriptions', status: 'success' });
    } catch (err) {
      dispatch({ notificationKey: 'countySubscriptions', status: 'error' });
      throw err;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testNetworkConnection = useCallback(async () => {
    try {
      const { connected } = await Network.getStatus();
      if (!connected) throw new Error();
      dispatch({ notificationKey: 'networkConnection', status: 'success' });
    } catch (err) {
      dispatch({ notificationKey: 'networkConnection', status: 'error' });
      throw err;
    }
  }, []);

  const testSendNotification = useCallback(async () => {
    try {
      await API.post('/notifications/test_notification/', { pushToken });
      dispatch({ notificationKey: 'sendNotification', status: 'success' });
    } catch (err) {
      dispatch({ notificationKey: 'sendNotification', status: 'error' });
      throw err;
    }
  }, [pushToken]);

  const testCriticalAlert = useCallback(async () => {
    try {
      setCriticalAlertStatus('pending');
      await API.post('/notifications/test_notification/', {
        pushToken,
        critical: true,
      });
      setCriticalAlertStatus('success');
    } catch (err) {
      setCriticalAlertStatus('error');
    }
  }, [pushToken]);

  const testNotifications = useCallback(async () => {
    try {
      dispatch({ type: 'init' });
      await testNotificationSettings();
      await testDeviceRegistration();
      await testCountySubscriptions();
      await testNetworkConnection();
      await testSendNotification();
      dispatch({ type: 'end' });
    } catch (err) {
      dispatch({ type: 'end' });
    }
  }, [
    testCountySubscriptions,
    testDeviceRegistration,
    testNotificationSettings,
    testNetworkConnection,
    testSendNotification,
  ]);

  useEffect(() => {
    testNotifications();
  }, [testNotifications]);

  const { testCount, notificationsState } = state;

  const notificationStateKeys = Object.keys(
    notificationsState,
  ) as NotificationKeys[];

  const errorNotificationKey = notificationStateKeys.find((notifKey) =>
    ['idle', 'error'].includes(notificationsState[notifKey]),
  );

  const criticalLoading = criticalAlertStatus === 'pending';
  const loading = notificationStateKeys.some(
    (notifKey) => notificationsState[notifKey] === 'pending',
  );

  const success = notificationStateKeys.every((notifKey) => {
    if (notifKey === 'countySubscriptions') {
      return ['idle', 'success'].includes(notificationsState[notifKey]);
    }
    return notificationsState[notifKey] === 'success';
  });

  const hasCountySubscriptions = countySubscriptions.length > 0;
  const hasWeatherAlerts = alerts.length > 0;

  return (
    <Container
      sx={{
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        overflowY: 'auto',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Stack spacing={2}>
        <NotificationItem
          title={t('testNotifications.tests.notificationSettings')}
          status={notificationsState.notificationSettings}
        />

        {showAlertStatus && (
          <>
            <EnabledItem
              title={t('testNotifications.tests.timeSensitiveEnabled')}
              enabled={timeSensitiveEnabled}
            />

            <EnabledItem
              title={t('testNotifications.tests.criticalAlertsEnabled')}
              enabled={criticalAlertsEnabled}
            />
          </>
        )}

        <NotificationItem
          title={t('testNotifications.tests.deviceRegistration')}
          status={notificationsState.deviceRegistration}
        />

        <NotificationItem
          title={t('testNotifications.tests.countySubscriptions')}
          status={notificationsState.countySubscriptions}
        />

        <NotificationItem
          title={t('testNotifications.tests.networkConnection')}
          status={notificationsState.networkConnection}
        />

        <NotificationItem
          title={t('testNotifications.tests.sendNotification')}
          status={notificationsState.sendNotification}
        />

        {showAlertStatus && criticalAlertsEnabled && (
          <NotificationItem
            title={t('testNotifications.tests.sendCriticalNotification')}
            status={criticalAlertStatus}
          />
        )}

        {errorNotificationKey && (
          <ErrorAlert notificationKey={errorNotificationKey} />
        )}

        {success && (
          <Alert
            icon={<CheckCircleIcon />}
            severity="success"
            data-testid="alert-success"
          >
            {(hasCountySubscriptions || hasWeatherAlerts) && (
              <Box sx={{ marginBottom: 2 }}>
                <Typography>
                  {t('testNotifications.alerts.success.notificationsFor')}
                </Typography>
                {hasCountySubscriptions && (
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {t('testNotifications.alerts.success.counties', {
                      count: countySubscriptions.length,
                    })}
                  </Typography>
                )}
                <WeatherAlertsMessage alerts={alerts} />
              </Box>
            )}
            <Typography>
              {t('testNotifications.alerts.success.note')}
            </Typography>
          </Alert>
        )}

        {showAlertStatusWarning && (
          <AlertStatusWarning
            criticalAlertsEnabled={criticalAlertsEnabled}
            timeSensitiveEnabled={timeSensitiveEnabled}
          />
        )}

        {testCount > 0 && (
          <Stack spacing={1}>
            <Button
              fullWidth
              size="large"
              onClick={testNotifications}
              disabled={loading || criticalLoading}
              color="neutral"
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                t('testNotifications.buttons.testAgain')
              )}
            </Button>

            {showAlertStatus && criticalAlertsEnabled && (
              <>
                <Button
                  fullWidth
                  size="large"
                  onClick={testCriticalAlert}
                  disabled={loading || criticalLoading}
                  color="neutral"
                >
                  {criticalLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('testNotifications.buttons.testCritical')
                  )}
                </Button>
                <Typography>
                  <Trans i18nKey="testNotifications.buttons.testCriticalNotes">
                    <strong>Note:</strong> Critical Alerts will bypass Do Not
                    Disturb, use a distinct sound, and may play at higher
                    volume.
                  </Trans>
                </Typography>
              </>
            )}

            <Typography variant="h3" sx={{ mt: 2 }}>
              <b>{t('testNotifications.troubleshooting.title')}</b>
            </Typography>
            <Typography variant="body2">
              {t('testNotifications.troubleshooting.description')}
            </Typography>

            <Button
              fullWidth
              size="large"
              onClick={() =>
                sendErrorReport(
                  getEmailLink({
                    notificationsState,
                    notificationSubscriptions: countySubscriptions,
                    user,
                    deviceInfo,
                    appInfo,
                    pushToken,
                    alerts,
                  }),
                )
              }
              disabled={loading || criticalLoading}
              color="neutral"
            >
              {t('testNotifications.buttons.sendReport')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Container>
  );
};
